// EventFilterPanel – filter events by search, status, and date range.

import React, { JSX } from 'react';
import type { EventFiltersValue } from '../types/EventsOverviewTypes';

type Props = {
    value: EventFiltersValue;
    onChange: (next: EventFiltersValue) => void;
};

// ---- Constants ----

const INPUT =
    'h-9 px-3 text-xs border rounded bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200';

const LABEL = 'text-[11px] font-semibold text-gray-700 dark:text-slate-300';
const HELP = 'text-[11px] text-gray-500 dark:text-slate-400';

// ---- Helpers ----

function setField<T extends keyof EventFiltersValue>(
    value: EventFiltersValue,
    key: T,
    v: EventFiltersValue[T],
): EventFiltersValue {
    return { ...value, [key]: v };
}

// ---- Component ----

export default function EventsFiltersPanel(props: Props): JSX.Element {
    const { value, onChange } = props;

    // Reset all filter fields to defaults.
    function clearAll(): void {
        onChange({
            search: '',
            status: 'any',
            startFrom: '',
            startTo: '',
            createdFrom: '',
            createdTo: '',
        });
    }

    return (
        <div className="space-y-4">
            <div className="space-y-1.5">
                <div className={LABEL}>Search</div>
                <input
                    className={INPUT}
                    placeholder="Name or eventId…"
                    value={value.search}
                    onChange={(e) => onChange(setField(value, 'search', e.target.value))}
                />
                <div className={HELP}>Matches name and eventId.</div>
            </div>

            <div className="space-y-1.5">
                <div className={LABEL}>Status</div>
                <select
                    className={INPUT}
                    value={value.status}
                    onChange={(e) => onChange(setField(value, 'status', e.target.value))}
                >
                    <option value="any">Any</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="DRAFT">DRAFT</option>
                    <option value="ENDED">ENDED</option>
                    <option value="ARCHIVED">ARCHIVED</option>
                    <option value="UNKNOWN">UNKNOWN</option>
                </select>
            </div>

            <div className="space-y-2">
                <div className={LABEL}>Start time</div>
                <div className="grid grid-cols-1 gap-2">
                    <input
                        type="datetime-local"
                        className={INPUT}
                        value={value.startFrom}
                        onChange={(e) => onChange(setField(value, 'startFrom', e.target.value))}
                    />
                    <input
                        type="datetime-local"
                        className={INPUT}
                        value={value.startTo}
                        onChange={(e) => onChange(setField(value, 'startTo', e.target.value))}
                    />
                </div>
                <div className={HELP}>Filters by event startAt.</div>
            </div>

            <div className="space-y-2">
                <div className={LABEL}>Created time</div>
                <div className="grid grid-cols-1 gap-2">
                    <input
                        type="datetime-local"
                        className={INPUT}
                        value={value.createdFrom}
                        onChange={(e) => onChange(setField(value, 'createdFrom', e.target.value))}
                    />
                    <input
                        type="datetime-local"
                        className={INPUT}
                        value={value.createdTo}
                        onChange={(e) => onChange(setField(value, 'createdTo', e.target.value))}
                    />
                </div>
                <div className={HELP}>Filters by event createdAt.</div>
            </div>

            <div className="pt-2">
                <button
                    type="button"
                    className="w-full h-9 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-semibold hover:bg-gray-50"
                    onClick={clearAll}
                >
                    Clear filters
                </button>
            </div>
        </div>
    );
}
